import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet, Button, DESKTOP_QUERY, useMediaQuery } from '@/core/ui';
import { cx } from '@/core/utils';
import { useMapEvent } from '@/features/map';
import { SearchBox } from '@/features/search';
import { SlidersIcon } from './icons';
import { PanelSettings, PanelSummary } from './PanelContent';

interface MapInsets {
  top: number;
  bottom: number;
  left: number;
}

/** Publishes how much of the map the UI covers, so MapLibre's controls stay visible (see mapControls.scss). */
function useMapInsets({ top, bottom, left }: MapInsets) {
  useEffect(() => {
    const style = document.documentElement.style;
    style.setProperty('--map-inset-top', `${top}px`);
    style.setProperty('--map-inset-bottom', `${bottom}px`);
    style.setProperty('--map-inset-left', `${left}px`);
  }, [top, bottom, left]);
}

/** Bottom edge (or right edge) of an element, kept up to date. */
function useEdge<T extends HTMLElement>(edge: 'bottom' | 'right') {
  const ref = useRef<T>(null);
  const [px, setPx] = useState(0);
  useEffect(() => {
    const el = ref.current!;
    const observer = new ResizeObserver(() => setPx(Math.ceil(el.getBoundingClientRect()[edge])));
    observer.observe(el);
    return () => observer.disconnect();
  }, [edge]);
  return [ref, px] as const;
}

/** Phones and tablets: search floats at the top, everything else lives in a bottom sheet. */
function MobileLayout() {
  const { t } = useTranslation('app');
  const [expanded, setExpanded] = useState(false);
  const [peek, setPeek] = useState(0);
  const [searchRef, searchBottom] = useEdge<HTMLDivElement>('bottom');
  useMapInsets({ top: searchBottom, bottom: peek, left: 0 });
  // Working on the map: get the sheet out of the way.
  useMapEvent('click', () => setExpanded(false));

  return (
    <>
      <div
        ref={searchRef}
        className="absolute inset-x-0 top-0 z-3 pt-[max(0.75rem,env(safe-area-inset-top))] pr-[max(0.75rem,env(safe-area-inset-right))] pl-[max(0.75rem,env(safe-area-inset-left))]"
      >
        <SearchBox />
      </div>
      <BottomSheet
        label={t('panel')}
        expandLabel={t('showSettings')}
        collapseLabel={t('hideSettings')}
        expanded={expanded}
        onExpandedChange={setExpanded}
        onPeekHeightChange={setPeek}
        header={
          <PanelSummary
            actions={
              <Button
                className={cx('w-10 px-0', expanded && 'border-accent bg-accent-soft text-accent')}
                aria-label={expanded ? t('hideSettings') : t('showSettings')}
                aria-expanded={expanded}
                onClick={() => setExpanded(!expanded)}
              >
                <SlidersIcon />
              </Button>
            }
          />
        }
      >
        <PanelSettings />
      </BottomSheet>
    </>
  );
}

/** Wide screens: one side panel with everything. */
function DesktopLayout() {
  const { t } = useTranslation('app');
  const [panelRef, panelRight] = useEdge<HTMLElement>('right');
  useMapInsets({ top: 0, bottom: 0, left: panelRight });

  return (
    <aside
      ref={panelRef}
      aria-label={t('panel')}
      className="absolute top-3 left-3 z-2 flex max-h-[calc(100dvh-1.5rem)] w-[340px] flex-col rounded-2xl bg-white/95 shadow-[0_2px_12px_rgba(0,0,0,.2)] backdrop-blur"
    >
      <div className="shrink-0 p-4 pb-3">
        <SearchBox className="mb-3" />
        <PanelSummary />
      </div>
      <div className="min-h-0 overflow-y-auto px-4">
        <PanelSettings />
      </div>
    </aside>
  );
}

export function ControlPanel() {
  return useMediaQuery(DESKTOP_QUERY) ? <DesktopLayout /> : <MobileLayout />;
}
